import { useTheme } from '@rneui/themed'
import { cleanUrl } from 'chat/messages/queries/shared/utils'
import { showShareHeaderButton } from 'chat/shared/header_share_button'
import * as FileSystem from 'expo-file-system/legacy'
import * as IntentLauncher from 'expo-intent-launcher'
import * as Sharing from 'expo-sharing'
import { useFileDetails } from 'file_folder_repository/queries/details'
import { useFileContent } from 'file_folder_repository/queries/file_content'
import { FileViewerScreenProps } from 'home/stack/types'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { Linking, Platform } from 'react-native'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderTitle } from 'shared/header/header_title'
import { shareAttachment } from 'shared/utils'
import { FileViewerView } from './view'

export function FileViewerScreen({ navigation, route }: FileViewerScreenProps) {
  const { fileId, versionNumber: versionNumberParam } = route.params
  const fileDetailsQuery = useFileDetails(fileId)
  const [versionNumber, setVersionNumber] = useState<number | undefined>(
    versionNumberParam
  )
  const [error, setError] = useState<Error | undefined>()
  const { theme } = useTheme()
  const [isShareLoading, setShareLoading] = useState(false)

  const selectedFileVersion = useMemo(() => {
    if (!versionNumber) {
      return fileDetailsQuery.data?.currentVersion
    }
    return fileDetailsQuery.data?.currentVersion?.versionNumber ===
      versionNumber
      ? fileDetailsQuery.data?.currentVersion
      : fileDetailsQuery.data?.fileVersions?.find(
          fileVersion => fileVersion.versionNumber === versionNumber
        )
  }, [
    fileDetailsQuery.data?.currentVersion,
    fileDetailsQuery.data?.fileVersions,
    versionNumber,
  ])

  const isExternalUrl = !!selectedFileVersion?.externalUrl

  const fileContentQuery = useFileContent(
    fileId,
    versionNumber,
    selectedFileVersion?.fileMimeType,
    selectedFileVersion?.url,
    !isExternalUrl
  )

  const onShareAttachment = useCallback(async () => {
    setShareLoading(true)
    const url = fileContentQuery.data?.url
    const mimeType = fileContentQuery.data?.contentType
    const name = fileDetailsQuery.data?.name
    if (url && mimeType && name) {
      await shareAttachment({
        name,
        uri: url,
        mimeType,
      })
    }
    setShareLoading(false)
  }, [
    fileContentQuery.data?.contentType,
    fileContentQuery.data?.url,
    fileDetailsQuery.data?.name,
  ])

  useEffect(() => {
    if (!versionNumber) {
      if (selectedFileVersion?.versionNumber) {
        setVersionNumber(selectedFileVersion?.versionNumber)
      }
    }
  }, [
    fileDetailsQuery.data?.currentVersion?.versionNumber,
    selectedFileVersion?.versionNumber,
    versionNumber,
  ])

  useLayoutEffect(() => {
    if (fileDetailsQuery.data?.name) {
      showHeaderTitle(navigation, fileDetailsQuery.data?.name)
    }
  }, [navigation, fileDetailsQuery.data?.name])

  useLayoutEffect(() => {
    const url = fileContentQuery.data?.url
    const mimeType = fileContentQuery.data?.contentType
    const name = fileDetailsQuery.data?.name
    if (url && mimeType && name) {
      showShareHeaderButton({
        navigation,
        theme,
        onPress: onShareAttachment,
        loading: isShareLoading,
      })
    }
  }, [
    fileContentQuery.data?.contentType,
    fileContentQuery.data?.url,
    fileDetailsQuery.data?.name,
    isShareLoading,
    navigation,
    onShareAttachment,
    theme,
  ])

  const onOpenExternalFile = useCallback(async () => {
    const externalUrl = fileDetailsQuery.data?.currentVersion?.externalUrl
    if (externalUrl) {
      try {
        const cleanedUrl = cleanUrl(externalUrl)
        await Linking.openURL(cleanedUrl)
      } catch (e) {
        setError(e)
      }
    }
  }, [fileDetailsQuery.data?.currentVersion?.externalUrl])

  const onOpenFile = useCallback(async () => {
    const url = fileContentQuery.data?.url
    const mimeType = fileContentQuery.data?.contentType
    if (url && mimeType) {
      try {
        const contentUri = await FileSystem.getContentUriAsync(url)
        if (Platform.OS === 'android') {
          await IntentLauncher.startActivityAsync(
            'android.intent.action.VIEW',
            {
              data: contentUri,
              flags: 1,
              type: mimeType,
            }
          )
        } else if (Platform.OS === 'ios') {
          await Sharing.shareAsync(contentUri)
        } else if (Platform.OS === 'web') {
          window.open(url, '_blank')
        }
      } catch (e) {
        console.log('error', e)
      }
    }
  }, [fileContentQuery.data?.contentType, fileContentQuery.data?.url])

  return (
    <StatusBoundary
      error={fileDetailsQuery.error || fileContentQuery.error || error}
      isLoading={
        fileDetailsQuery.isLoading || fileContentQuery.isLoading
      }
    >
      <FileViewerView
        file={fileDetailsQuery.data}
        url={fileContentQuery.data?.url}
        mimeType={fileContentQuery.data?.contentType}
        onError={setError}
        onOpenWebLink={onOpenExternalFile}
        onOpenFile={onOpenFile}
        onGoBack={() => navigation.goBack()}
      />
    </StatusBoundary>
  )
}
